import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Quote, Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

/**
 * Real Wikipedia client references — each testimonial is tied to a live
 * Wikipedia page the Prime Wiki Services team has worked on. A "Visit"
 * link on each card opens the corresponding Wikipedia article.
 */
export const DEFAULT_TESTIMONIALS = [
  {
    name: "Devon Archer",
    role: "Private Investor & Former Board Member, Rosemont Seneca Partners",
    quote: "My public profile attracts constant scrutiny, so every citation on the page had to hold up. Prime Wiki Services handled the sourcing with forensic care — it has read cleanly since publication.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Devon_Archer",
  },
  {
    name: "Alexander Bafer",
    role: "Chairman, Brookmount Explorations",
    quote: "Leading a publicly traded resources company means every line about you online has to be defensible. Prime Wiki Services' notability audit saved us weeks of back-and-forth, and the page launched policy-clean.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Alexander_Bafer",
  },
  {
    name: "Robert A. Berman",
    role: "Healthcare Executive & Former CEO",
    quote: "Years of fragmented press coverage left the public story of my career incomplete. The Prime Wiki Services team consolidated everything into a single, independently sourced page that finally reads the way it should.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Robert_A._Berman",
  },
  {
    name: "Harold Brierley",
    role: "Founder & Chairman Emeritus, Brierley+Partners",
    quote: "I've built a four-decade career in customer-loyalty marketing, and I know craft when I see it. These editors write like real encyclopedists — no marketing tone, no inflated claims. Recommended.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Harold_Brierley",
  },
  {
    name: "Dennis Xavier DeSouza",
    role: "Entrepreneur & Business Leader",
    quote: "Building an online presence turned out to be harder than building the business itself. Prime Wiki Services rebuilt my draft from scratch and walked it through AfC cleanly on the first submission.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Dennis_Xavier_DeSouza",
  },
  {
    name: "Andy Kleitsch",
    role: "CEO & Co-founder, Atomo Coffee",
    quote: "As a food-tech startup we needed our Wikipedia presence to reflect independent science reporting — not our pitch deck. The citation strategy they built held up on the first review.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Atomo",
  },
  {
    name: "Bahadır Özdemir",
    role: "Founder & CEO, Airalo",
    quote: "Growing across 40+ countries meant our press was scattered everywhere. Prime Wiki Services consolidated years of coverage into a single neutral page that was approved without a single revision request.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Airalo",
  },
  {
    name: "Stefan Ortmanns",
    role: "CEO, Cerence Inc.",
    quote: "An automotive-AI company lives and dies on trust. Our Wikipedia page had to read encyclopedically, never promotionally — and that's exactly what the team delivered.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Cerence",
  },
  {
    name: "Erik Brooks",
    role: "Founder & CEO, Ethos Capital",
    quote: "In private equity, perception is currency. Prime Wiki Services understood every policy risk before drafting and kept our page defensible through multiple reviewer challenges. Genuinely impressive work.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Ethos_Capital",
  },
  {
    name: "Arvind Jain",
    role: "Co-founder & CEO, Glean Technologies",
    quote: "Enterprise AI is crowded and every claim attracts scrutiny. What set Prime Wiki Services apart was how rigorously they sourced each sentence — reviewers didn't flag a single citation on first pass.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/Glean_Technologies",
  },
  {
    name: "Marc Newson",
    role: "Partner & Designer, LoveFrom",
    quote: "A design studio's credibility lives in the details. Working with these editors felt like working with peers — meticulous, tasteful and obsessed with getting every reference exactly right.",
    stars: 5,
    wiki: "https://en.wikipedia.org/wiki/LoveFrom",
  },
];

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const chunk = (arr, size) => {
  if (size <= 0) return [arr];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Single, brand-consistent accent used for every testimonial card.
const CARD_ACCENT = "from-emerald-500 to-teal-600";

const Card = ({ t }) => (
  <figure className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-[0_20px_45px_-25px_rgba(10,37,64,0.35)] ring-1 ring-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(16,185,129,0.35)] hover:ring-emerald-200">
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${CARD_ACCENT}`} />

    <div className="relative flex items-center gap-1 text-amber-500">
      {Array.from({ length: t.stars }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-amber-400" />
      ))}
    </div>

    <blockquote className="relative mt-3 flex-1 text-[13.5px] leading-relaxed text-slate-700">
      <Quote className="mb-1.5 h-4 w-4 text-emerald-500/70" />
      {t.quote}
    </blockquote>

    <figcaption className="relative mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br ${CARD_ACCENT} font-serif text-xs font-semibold text-white shadow-md`}>
        {initials(t.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-[#0A2540]">{t.name}</div>
        <div className="truncate text-[11px] text-slate-500">{t.role}</div>
      </div>
    </figcaption>

    {t.wiki ? (
      <a
        href={t.wiki}
        target="_blank"
        rel="noopener noreferrer"
        className="relative mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
        data-testid={`testimonial-visit-${initials(t.name).toLowerCase()}`}
      >
        Visit Wikipedia page <ExternalLink className="h-3 w-3" />
      </a>
    ) : null}
  </figure>
);

/**
 * TestimonialCarousel — 3 cards per page on md+, 1 per page on mobile.
 * Auto-advances in groups of 3, with manual prev/next, dots and swipe.
 */
export const TestimonialCarousel = ({
  items = DEFAULT_TESTIMONIALS,
  eyebrow = "Testimonials",
  title = "Clients who chose credibility.",
  subtitle = "400+ published pages. Here's what clients say when the dust has settled and the page is still live.",
  autoPlayMs = 5500,
}) => {
  const [perPage, setPerPage] = useState(3);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setPerPage(mq.matches ? 3 : 1);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const pages = useMemo(() => chunk(items, perPage), [items, perPage]);
  const pageCount = pages.length;

  useEffect(() => {
    setIndex((i) => (pageCount ? i % pageCount : 0));
  }, [pageCount]);

  const goto = useCallback((i) => setIndex(((i % pageCount) + pageCount) % pageCount), [pageCount]);
  const next = useCallback(() => goto(index + 1), [goto, index]);
  const prev = useCallback(() => goto(index - 1), [goto, index]);

  useEffect(() => {
    if (paused || pageCount <= 1) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % pageCount), autoPlayMs);
    return () => clearInterval(id);
  }, [paused, pageCount, autoPlayMs]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  return (
    <section
      className="relative overflow-hidden py-20"
      style={{ background: "linear-gradient(180deg,#0A2540 0%,#0e2c4a 60%,#0A2540 100%)" }}
      data-testid="testimonial-carousel"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(600px 320px at 15% 20%, rgba(16,185,129,0.25) 0%, transparent 60%), radial-gradient(500px 280px at 85% 80%, rgba(59,130,246,0.22) 0%, transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300 ring-1 ring-emerald-500/30">
            {eyebrow}
          </span>
          <h2 className="font-serif mt-4 text-3xl font-semibold text-white sm:text-4xl">{title}</h2>
          {subtitle ? <p className="mt-3 text-sm text-white/70 sm:text-base">{subtitle}</p> : null}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs">
          {[
            { v: "4.9 / 5", l: "Average rating" },
            { v: "11", l: "Featured live pages" },
            { v: "92%", l: "AfC acceptance" },
          ].map((s) => (
            <div
              key={s.l}
              className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 text-white ring-1 ring-white/10 backdrop-blur"
            >
              <span className="font-serif text-sm text-emerald-300">{s.v}</span>
              <span className="text-white/70">{s.l}</span>
            </div>
          ))}
        </div>

        <div
          className="relative mt-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-label="Client testimonials"
          data-testid="testimonial-carousel-track"
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {pages.map((group, pIdx) => (
                <div key={pIdx} className="w-full shrink-0 px-1">
                  <div className={`grid gap-6 ${perPage === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3"}`}>
                    {group.map((t, i) => (
                      <Card key={`${pIdx}-${i}`} t={t} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={prev}
            aria-label="Previous testimonials"
            className="absolute left-0 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20 md:-left-4 md:inline-flex"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next testimonials"
            className="absolute right-0 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20 md:-right-4 md:inline-flex"
            data-testid="testimonial-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2" data-testid="testimonial-dots">
          {pages.map((_, i) => (
            <button
              type="button"
              key={i}
              onClick={() => goto(i)}
              aria-label={`Go to testimonial page ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-emerald-400" : "w-2 bg-white/30 hover:bg-white/50"
              }`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>

        <p className="mt-4 text-center text-xs text-white/50">
          Auto-advances every {Math.round(autoPlayMs / 1000)}s · swipe or hover to pause
        </p>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
